import React, { VFC } from 'react';

import { IntegrationApiKey } from 'src/common/interfaces/integration-api-key.interface';
import { IntegrationOauth } from 'src/common/interfaces/integration-oauth.interface';
import { Integration } from 'src/common/interfaces/integration.interface';
import { EditStrongIcon } from 'src/monet/icons';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentUser } from 'src/store/user';
import { IntegrationIconMapper } from '../settings/integrations/card';
import { DashboardIntegrationsConnectionCard } from './connection/card';

export type IntegrationKeyType = IntegrationApiKey | IntegrationOauth;

interface DashboardIntegrationListComponentProps {
  keys?: IntegrationKeyType[];
  isParagonAuthenticated: boolean;
  onParagonConfigureClick: () => void;
}

export interface IntegrationDisconnectParams<T> {
  name: string;
  stack: T;
  type: 'api-key' | 'oauth';
}

export const DashboardIntegrationsListFactory = (
  provider: Integration,
  onDisconnect: (params: IntegrationDisconnectParams<IntegrationKeyType>) => void
): VFC<DashboardIntegrationListComponentProps> => {
  const { code, name, is_paragon, is_api_key, is_oauth, is_static } = provider;
  const userApiKey = useCurrentUser((state) => state.currentUser!.api_key);
  const organizationId = useCurrentOrganization((state) => state.currentOrganization!.id);

  const IntegrationIcon = IntegrationIconMapper[code];
  if (is_static) {
    return () => (
      <div className='flex flex-col space-y-2 text-bedrock-black'>
        <h3 className='text-bedrock-p-strong'>Your details</h3>
        <div className='flex items-center space-x-2 px-2'>
          <div className='flex flex-shrink-0'>
            <IntegrationIcon className='w-5 h-5' />
          </div>
          <div className='flex flex-col space-y-1'>
            <p className='text-bedrock-p'>Organization ID: {organizationId}</p>
            <p className='text-bedrock-p'>API Key: {userApiKey}</p>
          </div>
        </div>
      </div>
    );
  } else if (is_api_key) {
    return ({ keys = [] }) => (
      <>
        {keys.length > 0 && (
          <DashboardIntegrationsConnectionCard
            keys={keys as IntegrationApiKey[]}
            provider={provider}
            type='api-key'
            icon={<IntegrationIcon className='w-5 h-5' />}
            renderContent={(key) => (
              <div className='text-bedrock-p truncate'>
                <span>Key: </span>
                <span>{key.api_key}</span>
              </div>
            )}
            onDisconnect={onDisconnect}
          />
        )}
      </>
    );
  } else if (is_oauth) {
    return ({ keys = [] }) =>
      keys.length > 0 ? (
        <DashboardIntegrationsConnectionCard
          keys={keys as IntegrationOauth[]}
          type='oauth'
          provider={provider}
          icon={<IntegrationIcon className='w-5 h-5' />}
          renderContent={(key) => <div className='text-bedrock-p truncate'>{key.email}</div>}
          onDisconnect={onDisconnect}
        />
      ) : null;
  } else if (is_paragon) {
    return ({ isParagonAuthenticated, onParagonConfigureClick }) => (
      <>
        {isParagonAuthenticated && (
          <DashboardIntegrationsConnectionCard
            keys={[name as unknown as IntegrationKeyType]}
            type='oauth'
            provider={provider}
            icon={<IntegrationIcon className='w-5 h-5' />}
            renderContent={(key) => <div className='text-bedrock-p-strong space-x-1'>{key}</div>}
            menuItem={
              <div className='flex items-center space-x-sm'>
                <EditStrongIcon />
                <span className='text-bedrock-p text-bedrock-black'>Configure</span>
              </div>
            }
            onMenuItemClick={onParagonConfigureClick}
            onDisconnect={onDisconnect}
          />
        )}
      </>
    );
  }

  return () => null;
};
